/**
 * Custom Header Menu – desktop + mobile interactions.
 *
 * Custom elements:
 *   <custom-header-menu>  – desktop (normal | mega-menu | drawer)
 *   <custom-header-aside> – mobile slide-in drawer
 *   <custom-header-dropdown> – mobile accordion dropdown
 */

/* ─── Desktop menu ─────────────────────────────────────────────────────── */

class CustomHeaderMenu extends HTMLElement {
  /** @type {HTMLElement|null} */ #activeItem = null;
  /** @type {ReturnType<typeof setTimeout>|null} */ #closeTimer = null;
  #gracePeriod = 150;

  connectedCallback() {
    this.#gracePeriod = parseInt(this.dataset.gracePeriod || '150', 10);

    this.querySelectorAll('.chm__item--has-children').forEach((item) => {
      item.addEventListener('mouseenter', () => this.#open(item));
      item.addEventListener('mouseleave', () => this.#scheduleClose());
    });

    // Keep open when hovering over the panel itself
    this.querySelectorAll(
      '.chm__mega, .chm__dropdown, .chm__drawer-panel'
    ).forEach((panel) => {
      panel.addEventListener('mouseenter', () => this.#cancelClose());
      panel.addEventListener('mouseleave', () => this.#scheduleClose());
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#close();
    });

    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    clearTimeout(this.#closeTimer);
  }

  #onDocumentClick = (e) => {
    if (!(e.target instanceof Node) || !this.contains(e.target)) this.#close();
  };

  #open(item) {
    this.#cancelClose();
    if (this.#activeItem === item) return;
    if (this.#activeItem) this.#deactivate(this.#activeItem);

    this.#activeItem = item;
    item.classList.add('is-open');
    item.querySelector('.chm__top-link')?.setAttribute('aria-expanded', 'true');
  }

  #scheduleClose() {
    this.#closeTimer = setTimeout(() => this.#close(), this.#gracePeriod);
  }

  #cancelClose() {
    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #close() {
    if (!this.#activeItem) return;
    this.#deactivate(this.#activeItem);
    this.#activeItem = null;
  }

  #deactivate(item) {
    item.classList.remove('is-open');
    item.querySelector('.chm__top-link')?.setAttribute('aria-expanded', 'false');
  }
}

/* ─── Mobile aside (slide-in from left) ───────────────────────────────── */

class CustomHeaderAside extends HTMLElement {
  /**
   * Blocks overlay-triggered close briefly after open.
   * Prevents ghost-click / tap-through on mobile.
   */
  #preventClose = false;
  /** @type {((e: KeyboardEvent) => void) | null} */
  #keydownHandler = null;

  connectedCallback() {
    // stopPropagation stops the tap event from bubbling to any parent
    // handler that might close the menu (e.g. Shopify's section rendering).
    this.querySelector('.chm-aside__toggle')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.open();
    });

    this.querySelector('.chm-aside__close')?.addEventListener('click', () => this.close());

    this.querySelector('.chm-aside__overlay')?.addEventListener('click', () => {
      if (!this.#preventClose) this.close();
    });

    this.querySelectorAll('.chm-aside__expand').forEach((btn) => {
      btn.addEventListener('click', () => this.#toggleSub(btn));
    });

    this.#keydownHandler = (e) => {
      if (e.key === 'Escape' && this.#isOpen) this.close();
    };
    document.addEventListener('keydown', this.#keydownHandler);
  }

  disconnectedCallback() {
    if (this.#keydownHandler) {
      document.removeEventListener('keydown', this.#keydownHandler);
      this.#keydownHandler = null;
    }
    document.body.style.overflow = '';
  }

  get #isOpen() {
    return this.querySelector('.chm-aside__panel')?.getAttribute('aria-hidden') !== 'true';
  }

  open() {
    const panel = this.querySelector('.chm-aside__panel');
    const overlay = this.querySelector('.chm-aside__overlay');
    if (!panel) return;

    panel.setAttribute('aria-hidden', 'false');
    panel.removeAttribute('inert');
    this.querySelector('.chm-aside__toggle')?.setAttribute('aria-expanded', 'true');
    overlay?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';

    // 600 ms guard absorbs ghost clicks AND any Shopify-internal synthetic
    // click events that fire shortly after the toggle tap on mobile.
    this.#preventClose = true;
    setTimeout(() => { this.#preventClose = false; }, 600);
  }

  close() {
    // Honour the ghost-click guard — if open() was called very recently, ignore
    if (this.#preventClose) return;
    const panel = this.querySelector('.chm-aside__panel');
    if (!panel) return;

    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');
    this.querySelector('.chm-aside__toggle')?.setAttribute('aria-expanded', 'false');
    this.querySelector('.chm-aside__overlay')?.classList.remove('is-visible');
    document.body.style.overflow = '';
  }

  #toggleSub(/** @type {HTMLElement} */ btn) {
    const id = btn.getAttribute('aria-controls');
    const target = id ? document.getElementById(id) : null;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    if (!target) return;
    if (expanded) {
      // Closing: pin to current height then animate to 0
      target.style.maxHeight = target.scrollHeight + 'px';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { target.style.maxHeight = '0'; });
      });
      target.addEventListener('transitionend', () => {
        target.setAttribute('hidden', '');
        target.style.maxHeight = '';
      }, { once: true });
    } else {
      // Opening: animate from 0 to content height, leave inline style set so
      // CSS max-height:0 does not re-trigger the closing transition.
      target.removeAttribute('hidden');
      target.style.maxHeight = '0';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { target.style.maxHeight = target.scrollHeight + 'px'; });
      });
    }
  }
}

/* ─── Mobile dropdown (accordion, no overlay) ─────────────────────────── */

class CustomHeaderDropdown extends HTMLElement {
  connectedCallback() {
    this.querySelector('.chm-dropdown__toggle')?.addEventListener('click', () =>
      this.#toggleMenu()
    );

    this.querySelectorAll('.chm-dropdown__expand').forEach((btn) => {
      btn.addEventListener('click', () => this.#toggleSub(btn));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#closeMenu();
    });

    document.addEventListener('click', this.#onOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onOutsideClick);
  }

  #onOutsideClick = (e) => {
    if (!(e.target instanceof Node) || !this.contains(e.target)) this.#closeMenu();
  };

  #toggleMenu() {
    const menu = /** @type {HTMLElement|null} */ (this.querySelector('.chm-dropdown__menu'));
    const toggle = this.querySelector('.chm-dropdown__toggle');
    if (!menu || !toggle) return;
    const hidden = menu.hasAttribute('hidden');
    if (hidden) {
      menu.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      menu.classList.add('is-animating');
    } else {
      this.#closeMenu();
    }
  }

  #closeMenu() {
    const menu = this.querySelector('.chm-dropdown__menu');
    const toggle = this.querySelector('.chm-dropdown__toggle');
    if (menu) { menu.setAttribute('hidden', ''); menu.classList.remove('is-animating'); }
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  #toggleSub(btn) {
    const id = btn.getAttribute('aria-controls');
    const target = id ? document.getElementById(id) : null;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    if (!target) return;
    if (expanded) {
      target.style.maxHeight = target.scrollHeight + 'px';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { target.style.maxHeight = '0'; });
      });
      target.addEventListener('transitionend', () => {
        target.setAttribute('hidden', '');
        target.style.maxHeight = '';
      }, { once: true });
    } else {
      target.removeAttribute('hidden');
      target.style.maxHeight = '0';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { target.style.maxHeight = target.scrollHeight + 'px'; });
      });
      // Leave maxHeight set — clearing it would retrigger CSS max-height:0 transition.
    }
  }
}

if (!customElements.get('custom-header-menu')) {
  customElements.define('custom-header-menu', CustomHeaderMenu);
}
if (!customElements.get('custom-header-aside')) {
  customElements.define('custom-header-aside', CustomHeaderAside);
}
if (!customElements.get('custom-header-dropdown')) {
  customElements.define('custom-header-dropdown', CustomHeaderDropdown);
}

/* ─── Menu-style sync (replaces header-menu.js resize handler) ────────── */

/**
 * Mirror what utilities.js setHeaderMenuStyle() does, but without
 * depending on overflow-list. We set data-menu-style='drawer' on touch
 * devices and 'menu' on pointer devices, matching the original behaviour
 * when no overflow-list element is present.
 */
function syncMenuStyle() {
  const header = document.querySelector('#header-component');
  if (!(header instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    const isTouchDevice = navigator.maxTouchPoints > 0;
    header.dataset.menuStyle = isTouchDevice ? 'drawer' : 'menu';
  });
}

function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

// Run once on load and on every resize
document.addEventListener('DOMContentLoaded', syncMenuStyle);
window.addEventListener('resize', debounce(syncMenuStyle, 100));
